import { BarChart2, FileEdit, MoreHorizontal, Search, Send, Trash2 } from 'lucide-react'

import { useState } from 'react'

import { useToast } from '@/hooks/useToast'

import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { PageHeader } from '@/components/atoms'
import { StatCard } from '@/components/dashboard/StatCard'
import { CampaignDialog } from '@/components/marketing/CampaignDialog'
import { CampaignReportDialog } from '@/components/marketing/CampaignReportDialog'
import { SendTestDialog } from '@/components/marketing/SendTestDialog'

import { Campaign, Subscriber } from '@/types/marketing'

export const EmailDashboard = () => {
  const [activeTab, setActiveTab] = useState('campaigns')
  const { toast } = useToast()

  // Dialog States
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false)
  const [sendTestOpen, setSendTestOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)

  // Mock Data
  const [campaigns, setCampaigns] = useState<Campaign[]>([
    {
      id: '1',
      name: 'Summer Sale Announcement',
      subject: 'Huge savings on summer gear! ☀️',
      status: 'sent',
      type: 'promotional',
      audience: 'All Subscribers',
      sentAt: new Date('2024-06-01'),
      stats: {
        sent: 5420,
        opened: 2840,
        clicked: 450,
        bounced: 12,
      },
      createdAt: new Date('2024-05-28'),
      updatedAt: new Date('2024-06-01'),
    },
    {
      id: '2',
      name: 'Weekly Newsletter #42',
      subject: "This week's top trends",
      status: 'scheduled',
      type: 'newsletter',
      audience: 'Newsletter List',
      scheduledAt: new Date('2024-06-10T09:00:00'),
      createdAt: new Date('2024-06-05'),
      updatedAt: new Date('2024-06-05'),
    },
    {
      id: '3',
      name: 'Abandoned Cart Recovery',
      subject: 'You left something behind...',
      status: 'draft',
      type: 'transactional',
      audience: 'Cart Abandoners',
      createdAt: new Date('2024-06-07'),
      updatedAt: new Date('2024-06-07'),
    },
  ])

  const subscribers: Subscriber[] = [
    {
      id: '1',
      email: 'alice@example.com',
      firstName: 'Alice',
      lastName: 'Johnson',
      status: 'subscribed',
      tags: ['vip', 'customer'],
      joinedAt: new Date('2024-01-15'),
    },
    {
      id: '2',
      email: 'bob@example.com',
      firstName: 'Bob',
      status: 'unsubscribed',
      tags: ['newsletter'],
      joinedAt: new Date('2024-02-20'),
    },
  ]

  const getStatusTone = (status: Campaign['status']): BadgeTone => {
    switch (status) {
      case 'sent':
        return 'success'
      case 'scheduled':
        return 'info'
      case 'sending':
        return 'warning'
      default:
        return 'soft'
    }
  }

  // Handlers
  const handleCreateCampaign = (campaignData: Partial<Campaign>) => {
    if (selectedCampaign) {
      // Edit existing
      setCampaigns(
        campaigns.map((c) =>
          c.id === selectedCampaign.id ? { ...c, ...campaignData, updatedAt: new Date() } : c
        )
      )
      toast({
        title: 'Campaign Updated',
        description: 'Your campaign has been updated successfully.',
      })
    } else {
      // Create new
      const newCampaign: Campaign = {
        id: Math.random().toString(36).substr(2, 9),
        name: campaignData.name!,
        subject: campaignData.subject!,
        status: 'draft',
        type: campaignData.type || 'newsletter',
        audience: campaignData.audience || 'All Subscribers',
        createdAt: new Date(),
        updatedAt: new Date(),
        ...campaignData,
      }
      setCampaigns([newCampaign, ...campaigns])
      toast({
        title: 'Campaign Created',
        description: 'New campaign created successfully.',
      })
    }
    setSelectedCampaign(null)
  }

  const handleEditCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign)
    setCampaignDialogOpen(true)
  }

  const handleDeleteCampaign = (id: string) => {
    setCampaigns(campaigns.filter((c) => c.id !== id))
    toast({
      title: 'Campaign Deleted',
      description: 'The campaign has been removed.',
      variant: 'destructive',
    })
  }

  const handleSendTest = (email: string) => {
    toast({
      title: 'Test Email Sent',
      description: `Test email sent to ${email}`,
    })
  }

  const handleViewReport = (campaign: Campaign) => {
    if (campaign.status !== 'sent') {
      toast({
        title: 'Report Unavailable',
        description: 'Reports are only available for sent campaigns.',
        variant: 'destructive',
      })
      return
    }
    setSelectedCampaign(campaign)
    setReportOpen(true)
  }

  const statusLabel = (status: string) => status.charAt(0).toUpperCase() + status.slice(1)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Email marketing"
        description="Create campaigns and manage subscribers."
        onAddClick={() => {
          setSelectedCampaign(null)
          setCampaignDialogOpen(true)
        }}
        addButtonText="Create campaign"
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard title="Total subscribers" value="5,420" change="" trend="neutral" note="+125 this week" />
        <StatCard title="Average open rate" value="42.8%" change="2.4%" trend="up" note="vs last month" />
        <StatCard title="Emails sent (30 days)" value="24.5k" change="" trend="neutral" note="Across 4 campaigns" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <TabsList className="self-start">
                <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
                <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
              </TabsList>
              {activeTab === 'subscribers' && (
                <div className="relative w-full sm:w-64">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search subscribers" aria-label="Search subscribers" className="pl-8" />
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <TabsContent value="campaigns" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Performance</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-ink">{campaign.name}</span>
                          <span className="text-xs text-muted-foreground">{campaign.subject}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge tone={getStatusTone(campaign.status)}>{statusLabel(campaign.status)}</Badge>
                      </TableCell>
                      <TableCell>
                        {campaign.status === 'sent' && campaign.stats ? (
                          <div className="flex gap-4 text-sm">
                            <div className="flex flex-col">
                              <span className="font-medium tabular-nums text-ink">
                                {((campaign.stats.opened / campaign.stats.sent) * 100).toFixed(1)}%
                              </span>
                              <span className="text-xs text-muted-foreground">Open rate</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="font-medium tabular-nums text-ink">
                                {((campaign.stats.clicked / campaign.stats.opened) * 100).toFixed(1)}%
                              </span>
                              <span className="text-xs text-muted-foreground">Click rate</span>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums text-text-2">
                        {campaign.status === 'scheduled' && campaign.scheduledAt
                          ? `Scheduled ${campaign.scheduledAt.toLocaleDateString()}`
                          : campaign.sentAt
                            ? `Sent ${campaign.sentAt.toLocaleDateString()}`
                            : `Created ${campaign.createdAt.toLocaleDateString()}`}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Campaign actions">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditCampaign(campaign)}>
                              <FileEdit className="size-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setSendTestOpen(true)}>
                              <Send className="size-4" />
                              Send test
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleViewReport(campaign)}>
                              <BarChart2 className="size-4" />
                              View report
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-error"
                              onClick={() => handleDeleteCampaign(campaign.id)}
                            >
                              <Trash2 className="size-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="subscribers" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell className="text-sm font-medium text-ink">{subscriber.email}</TableCell>
                      <TableCell className="text-sm text-text-2">
                        {subscriber.firstName} {subscriber.lastName}
                      </TableCell>
                      <TableCell>
                        <Badge tone={subscriber.status === 'subscribed' ? 'success' : 'soft'}>
                          {statusLabel(subscriber.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {subscriber.tags.map((tag) => (
                            <Badge key={tag} tone="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums text-text-2">
                        {subscriber.joinedAt.toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" aria-label="Subscriber actions">
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>

      {/* Dialogs */}
      <CampaignDialog
        open={campaignDialogOpen}
        onOpenChange={setCampaignDialogOpen}
        campaign={selectedCampaign}
        onSave={handleCreateCampaign}
      />
      <SendTestDialog open={sendTestOpen} onOpenChange={setSendTestOpen} onSend={handleSendTest} />
      <CampaignReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        campaign={selectedCampaign}
      />
    </div>
  )
}
